// ── Enrichment Loader ─────────────────────────────────────────────────────────
// Fetches per-chapter enriched JSON and converts to slide format for EnhancedLearnMode.
// V2-APPLIED: supports schemaVersion "2.0" (priorKnowledge, primingQuestion, whyWrong, difficulty)
// V2.1-APPLIED: supports schemaVersion "2.1" (point.explainEn/Hi as array — bubble progression)
// while keeping v1 chapters rendering exactly as before.

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}

// Default config used as fallback if public/data/enrichment-config.json is absent or malformed.
// Clients should ship the JSON file; the hardcoded default is a safety net that matches v1 behavior.
const DEFAULT_CONFIG = {
  default: "v1",
  chapters: {},
  paths: {
    v1: "/data/enriched/",
    v2: "/data/enriched-v2/",
  },
};

let configPromise = null; // module-level memoized promise

/**
 * Fetch enrichment-config.json once. Subsequent calls return the cached config.
 * Returns a well-formed config even if the network fetch fails.
 */
export function loadEnrichmentConfig() {
  if (configPromise) return configPromise;
  configPromise = fetch("/data/enrichment-config.json")
    .then(r => r.ok ? r.json() : DEFAULT_CONFIG)
    .then(cfg => ({
      default: cfg.default || DEFAULT_CONFIG.default,
      chapters: cfg.chapters || {},
      paths: { ...DEFAULT_CONFIG.paths, ...(cfg.paths || {}) },
    }))
    .catch(() => DEFAULT_CONFIG);
  return configPromise;
}

/**
 * Compose the chapter key used in enrichment-config.json's "chapters" map
 * (same shape as the filename stem, without extension).
 */
function chapterKey(cls, subject, ncertChapter, chapterName) {
  return `class-${cls}-${slugify(subject)}-ch${ncertChapter}-${slugify(chapterName)}`;
}

/**
 * Public helper — same composition as the internal chapterKey(). Exposed so the
 * chapter list can determine which chapters have v2 enrichment available.
 */
export function getChapterKey(cls, subject, ncertChapter, chapterName) {
  return chapterKey(cls, subject, ncertChapter, chapterName);
}

/**
 * Build a v1-path URL. Kept for backward compatibility with any callers that
 * expected a synchronous URL.
 */
export function getEnrichedUrl(cls, subject, ncertChapter, chapterName) {
  return `/data/enriched/${chapterKey(cls, subject, ncertChapter, chapterName)}.json`;
}

/**
 * Async URL resolver that honors enrichment-config.json.
 * Returns the primary URL and a v1 fallback URL (for resilient fetch).
 */
export async function resolveEnrichedUrl(cls, subject, ncertChapter, chapterName) {
  const cfg = await loadEnrichmentConfig();
  const key = chapterKey(cls, subject, ncertChapter, chapterName);
  const version = cfg.chapters[key] || cfg.default || "v1";
  const base = cfg.paths[version] || cfg.paths.v1 || "/data/enriched/";
  const primary = `${base}${key}.json`;
  const fallback = `${cfg.paths.v1 || "/data/enriched/"}${key}.json`;
  return { primary, fallback, version };
}

/**
 * Fetch enriched data for a chapter. Returns null if not found.
 * V2-APPLIED: uses the config-resolved URL; falls back to v1 if a v2 file 404s.
 * Caches by final URL in memory so subsequent calls don't re-fetch.
 */
const cache = {};
export async function fetchEnrichedChapter(cls, subject, ncertChapter, chapterName) {
  const { primary, fallback } = await resolveEnrichedUrl(cls, subject, ncertChapter, chapterName);
  if (cache[primary] !== undefined) return cache[primary];

  try {
    const res = await fetch(primary);
    if (res.ok) {
      const data = await res.json();
      cache[primary] = data;
      return data;
    }
    // Primary miss — try v1 fallback if different
    if (fallback !== primary) {
      const fb = await fetch(fallback);
      if (fb.ok) {
        const data = await fb.json();
        cache[primary] = data;
        return data;
      }
    }
    cache[primary] = null;
    return null;
  } catch (e) {
    console.warn("Could not load enriched data:", primary, e);
    cache[primary] = null;
    return null;
  }
}

/**
 * Convert enriched concept data into slides array for EnhancedLearnMode
 * Maps enriched fields to slide types: hook, concept, formula, example, key-difference
 */
// Concept-to-diagram mapping — inject visual slides automatically
const VISUAL_MAP = {
  // Geometry — interactive preferred, static fallback
  "angle": ["interactive-angle"],
  "linear pair": ["interactive-angle"],
  "vertically opposite": ["interactive-angle"],
  "supplementary": ["linear-pair"],
  "parallel": ["parallel-transversal"],
  "types of triangle": ["triangle-types"],
  "congruence": ["triangle-types"],
  "isosceles": ["triangle-types"],
  "angle sum": ["angle-sum-property"],
  "pythagoras": ["interactive-pythagoras"],
  "pythagorean": ["interactive-pythagoras"],
  "similar triangle": ["similar-triangles"],
  "similarity": ["similar-triangles"],
  "basic proportionality": ["interactive-bpt"],
  "bpt": ["interactive-bpt"],
  "thales": ["interactive-bpt"],
  "ratio of areas": ["similar-triangles"],
  "parts of a circle": ["circle-parts"],
  "chord": ["circle-parts"],
  "circle theorem": ["circle-parts", "angle-semicircle"],
  "tangent": ["interactive-tangent"],
  "angle in a semicircle": ["angle-semicircle"],
  "cyclic": ["angle-semicircle"],
  "cartesian": ["interactive-coordinate"],
  "coordinate": ["interactive-coordinate"],
  "distance formula": ["interactive-distance"],
  "section formula": ["distance-formula"],
  "quadrilateral": ["quadrilateral-types"],
  "parallelogram": ["quadrilateral-types"],
  "mid-point theorem": ["quadrilateral-types"],
  "area of triangle": ["area-triangle-coord"],
  "heron": ["area-triangle-coord"],
  "sector": ["sector-area"],
  "segment": ["sector-area"],
  "areas related": ["sector-area"],
  "trigonometric ratio": ["interactive-trig"],
  "sin cos tan": ["interactive-trig"],
  "standard angle": ["interactive-trig"],
  "trigonometric identit": ["trig-ratios"],
  "height and distance": ["height-distance"],
  "angle of elevation": ["height-distance"],
  "angle of depression": ["height-distance"],
  // Light — interactive preferred, static fallback
  "reflection": ["interactive-reflection"],
  "concave mirror": ["concave-mirror"],
  "convex mirror": ["convex-mirror"],
  "curved mirror": ["concave-mirror", "convex-mirror"],
  "refraction of light": ["interactive-refraction"],
  "refraction by len": ["convex-lens"],
  "snell": ["interactive-refraction"],
  "convex lens": ["convex-lens"],
  "concave lens": ["convex-lens"],
  "dispersion": ["dispersion"],
  "prism": ["dispersion"],
  "human eye": ["human-eye"],
  "power of accommodation": ["human-eye"],
  "myopia": ["human-eye"],
  "hypermetropia": ["human-eye"],
  "defects of vision": ["human-eye"],
  // Electricity — interactive
  "ohm's law": ["interactive-ohms-law"],
  "ohm": ["interactive-ohms-law"],
  "electric current": ["interactive-ohms-law"],
  "resistance": ["interactive-ohms-law"],
  "series and parallel": ["interactive-ohms-law"],
  // Sound
  "sound as a wave": ["sound-wave"],
  "sound wave": ["sound-wave"],
  "compression": ["sound-wave"],
  "rarefaction": ["sound-wave"],
  "characteristics of sound": ["sound-wave"],
  "echo": ["sonar-echo"],
  "sonar": ["sonar-echo"],
  "ultrasound": ["sonar-echo"],
  // Magnetic Effects
  "magnetic field": ["magnetic-field-lines"],
  "field lines": ["magnetic-field-lines"],
  "solenoid": ["solenoid"],
  "electromagnet": ["solenoid"],
  "electric motor": ["electric-motor"],
  "electromagnetic induction": ["em-induction"],
  "faraday": ["em-induction"],
  // Atomic Structure
  "bohr": ["bohr-model"],
  "electronic configuration": ["bohr-model"],
  "subatomic": ["bohr-model"],
  "dalton": ["bohr-model"],
  "atomic mass": ["bohr-model"],
  // Cell Biology
  "cell:": ["cell-structure"],
  "plant cell": ["cell-structure"],
  "animal cell": ["cell-structure"],
  "cell wall": ["cell-structure"],
  "cytoplasm": ["cell-structure"],
  "organelle": ["cell-structure"],
  // 3D Shapes
  "cone": ["cone-sphere"],
  "sphere": ["cone-sphere"],
  "frustum": ["cone-sphere"],
  "surface area and volume": ["cone-sphere"],
  // Motion (from motion-slides)
  "equations of motion": ["velocity-time-graph"],
  "uniform circular": ["circular-motion"],
  "circular motion": ["circular-motion"],
  // Electric power (use ohm's law diagram)
  "electric power": ["interactive-ohms-law"],
};

function getVisualSlidesForConcept(conceptName) {
  const name = conceptName.toLowerCase();
  const visuals = [];
  const added = new Set();
  for (const [keyword, svgIds] of Object.entries(VISUAL_MAP)) {
    if (name.includes(keyword)) {
      for (const svgId of svgIds) {
        if (!added.has(svgId)) {
          added.add(svgId);
          visuals.push({ type: "visual", svgId, caption: "" });
        }
      }
    }
  }
  return visuals;
}

export function enrichedToSlides(enrichedChapter) {
  if (!enrichedChapter?.concepts) return null;

  // V2-APPLIED + V3-APPLIED: detect post-v1 schemas via major version >= 2.
  // Accepts "2.0", "2.1", "3.0", and any future major bump that keeps the same
  // enriched field shapes (priorKnowledge, primingQuestion, whyWrong, etc.).
  // v1 data has schemaVersion missing or "1.x" → falls through to v1 rendering.
  const sv = enrichedChapter.schemaVersion || "";
  const svMajor = typeof sv === "string" ? parseInt(sv.split(".")[0], 10) : NaN;
  const isV2 = !isNaN(svMajor) && svMajor >= 2;

  const slideData = {
    chapterId: `${slugify(enrichedChapter.subject)}-${enrichedChapter.class}-ch${enrichedChapter.ncertChapter}`,
    chapter: enrichedChapter.chapter,
    // CHAPTER-INTRO-APPLIED: forward chapter-level intro assets so the slide
    // builder in EnhancedLearnMode can insert the chapter-intro slide.
    chapterKey: enrichedChapter.chapterKey || null,
    introImage: enrichedChapter.introImage || null,
    introAudio: enrichedChapter.introAudio || null,
    subject: enrichedChapter.subject,
    class: enrichedChapter.class,
    schemaVersion: enrichedChapter.schemaVersion || "1.0",
    // QUESTION-BANK-APPLIED: pass through chapter-level revision set so
    // EnhancedLearnMode can splice the intro + items before chapter-complete.
    // Absent on older chapters (e.g. Light) → downstream guard is a no-op.
    questionBank: Array.isArray(enrichedChapter.questionBank) ? enrichedChapter.questionBank : null,
    concepts: [],
  };

  // V2.1-APPLIED + V3-APPLIED: boundary slides (concept-complete / break-time / chapter-complete)
  // are injected for v2.1 and any v3.x+ chapter. v2.0 chapters stay pre-boundary-slide to match
  // their original experience; v1 stays untouched.
  const isV21OrHigher = !isNaN(svMajor) && (svMajor >= 3 || sv === "2.1");
  const isV21 = isV21OrHigher;
  const totalConceptCount = enrichedChapter.concepts.length;

  enrichedChapter.concepts.forEach((concept, conceptIndex) => {
    const e = concept.enriched;
    if (!e) return; // skip concepts without enrichment (forEach continue)

    const slides = [];

    // V2-APPLIED: prior-knowledge bridge slide at the start of each concept (if provided)
    // Forward concept-level ncertScope/ncertNote so the renderer can show a small
    // "may appear in PYQs" badge on legacy concepts (Ch1 Real Numbers, etc.).
    // Concepts without these fields render unchanged.
    if (isV2 && concept.priorKnowledge) {
      slides.push({
        type: "prior-knowledge",
        text: concept.priorKnowledge,
        textHi: concept.priorKnowledgeHi || null,
        learningGoal: concept.learningGoal || null,
        conceptName: concept.name,
        ncertScope: concept.ncertScope || null,
        ncertNote: concept.ncertNote || null,
      });
    }

    // Collect micro-quiz questions for interleaving
    // V2-APPLIED: pass questionHi, difficulty, and whyWrong through to slides (v1 has none of these — no-op then)
    // HINGLISH-FIX-APPLIED: also carry explanationHi + whyWrongHi when the JSON provides them,
    // so the concept-check renderer can swap to Hindi when lang=hi.
    const microQs = (e.microQuiz || []).map((q, mIdx) => ({
      type: "concept-check",
      // MASTERY-WIRE-APPLIED: stable per-question index so concept-check renders
      // and any micro-quiz batch overflow share the same backend questionId.
      microQuizIndex: mIdx,
      question: q.question || "",
      questionHi: q.questionHi || null,
      options: q.options || [],
      optionsHi: Array.isArray(q.optionsHi) ? q.optionsHi : null,
      correct: q.correct ?? 0,
      explanation: q.explanation || "",
      explanationHi: q.explanationHi || null,
      whyWrong: Array.isArray(q.whyWrong) ? q.whyWrong : null,
      whyWrongHi: Array.isArray(q.whyWrongHi) ? q.whyWrongHi : null,
      difficulty: q.difficulty || null,
    }));
    let qIdx = 0; // tracks which question to insert next

    // 1. Hook slide — V2-APPLIED: carry options/correct/whyWrong when present for inline MCQ
    if (e.hook) {
      slides.push({
        type: "hook",
        emoji: e.hook.emoji || "💡",
        title: e.hook.title || "Think about this...",
        content: e.hook.content || "",
        question: e.hook.question || "",
        options: Array.isArray(e.hook.options) ? e.hook.options : null,
        correct: typeof e.hook.correct === "number" ? e.hook.correct : null,
        whyWrong: Array.isArray(e.hook.whyWrong) ? e.hook.whyWrong : null,
        hookAnswer: e.hookAnswer || null,
      });
    }

    // INTERACTIVE-CHALLENGE-APPLIED: per-concept timed challenge slide, inserted
    // right after the chapter hook MCQ. Supports Ch5-style (single number),
    // Ch4-style (twoNumbers), and Ch7-style (matchPairs). Concepts without the
    // field fall through unchanged.
    if (e.interactiveChallenge && typeof e.interactiveChallenge === "object") {
      const ic = e.interactiveChallenge;
      let resolvedIC = ic;
      // Resolve diagram.src to a full URL based on chapter context. The JSON
      // ships just the filename (e.g. "ch7-c1-quadrant-challenge.svg") and the
      // assets live under /diagrams/{subjectFolder}{class}/ch{ncert}/.
      if (ic.diagram && typeof ic.diagram.src === "string" && !ic.diagram.src.startsWith("/")) {
        const subjectFolderMap = { Maths: "math", Science: "science" };
        const subjFolder = subjectFolderMap[enrichedChapter.subject] || (enrichedChapter.subject || "").toLowerCase();
        const cls = enrichedChapter.class;
        const ncert = enrichedChapter.ncertChapter;
        const prefix = `/diagrams/${subjFolder}${cls}/ch${ncert}/`;
        resolvedIC = { ...ic, diagram: { ...ic.diagram, src: prefix + ic.diagram.src } };
      }
      slides.push({
        type: "interactive-challenge",
        challengeData: resolvedIC,
      });
    }

    // CONCEPT-TABLE-APPLIED: if this concept ships a reference table (e.g. Trig's
    // standard-angle values), show it once UP FRONT — before the teaching cards —
    // so the student has the reference in mind while the cards derive each row.
    // Concepts without visualSpec.tableData fall through unchanged (no-op).
    if (e.visualSpec && e.visualSpec.tableData && Array.isArray(e.visualSpec.tableData.headers) && Array.isArray(e.visualSpec.tableData.rows)) {
      slides.push({
        type: "concept-table",
        tableData: e.visualSpec.tableData,
        introLine: "Here's the table. The next few cards show where these values come from.",
      });
    }

    // CONCEPT-IMAGE-APPLIED: concept-level hero image as a standalone full-screen slide.
    // Shown after hookAnswer would close the loop, but in this codebase hookAnswer is
    // pushed at the END of the concept — so practically the hero appears right after
    // the chapter hook. Card-level images are NOT injected as standalone slides; they
    // embed inside priming/point sub-slides via the `embeddedImageUrl` prop below.
    if (concept.imageUrl) {
      slides.push({
        type: "concept-image",
        imageUrl: concept.imageUrl,
        caption: concept.name,
      });
    }

    // 2. Concept cards — INTERLEAVED with concept-check questions
    // Get matching diagram svgIds for this concept
    const conceptSvgIds = [];
    const addedSvgs = new Set();
    const cName = concept.name.toLowerCase();
    for (const [keyword, svgIds] of Object.entries(VISUAL_MAP)) {
      if (cName.includes(keyword)) {
        for (const svgId of svgIds) {
          if (!addedSvgs.has(svgId)) { addedSvgs.add(svgId); conceptSvgIds.push(svgId); }
        }
      }
    }

    if (e.concepts && e.concepts.length > 0) {
      e.concepts.forEach((c, ci) => {
        const points = (c.points || []).map(p => {
          if (typeof p === "string") return { text: p, explainEn: null, explainHi: null, showImage: false };
          return { text: p.text || "", explainEn: p.explainEn || null, explainHi: p.explainHi || null, showImage: p.showImage === true };
        });
        const title = c.title || concept.name;
        const icon = c.icon || "📚";

        // SECTION-MARKER-APPLIED: breath-moment slide BEFORE each concept card's content.
        // Only inserted when the card has a non-empty explicit title (c.title) — fallback to
        // concept.name would repeat the concept header on every card, defeating the purpose.
        // Rendered for every schema version since the data shape (c.title) exists in v1, v2, v2.1.
        if (c.title && typeof c.title === "string" && c.title.trim().length > 0) {
          slides.push({
            type: "section-marker",
            title: c.title.trim(),
            cardIndex: ci,
            totalCards: e.concepts.length,
          });
        }

        // CONCEPT-IMAGE-APPLIED: per the v3 schema with `showImage` flags, card-level
        // imageUrl is no longer injected as a standalone slide. Instead it embeds at
        // the top of priming/point sub-slides whose `showImage === true`. We compute
        // `embeddedImageUrl` per sub-slide below.
        const cardImageUrl = c.imageUrl || null;

        // V2-APPLIED: priming-question slide BEFORE the card's points
        // Encourages students to guess before learning — different from concept-check (after learning).
        if (isV2 && c.primingQuestion && Array.isArray(c.primingQuestion.options)) {
          slides.push({
            type: "priming-question",
            cardTitle: title,
            cardIcon: icon,
            question: c.primingQuestion.question || "",
            questionHi: c.primingQuestionHi?.question || null,
            options: c.primingQuestion.options || [],
            optionsHi: Array.isArray(c.primingQuestionHi?.options) ? c.primingQuestionHi.options : null,
            correct: typeof c.primingQuestion.correct === "number" ? c.primingQuestion.correct : 0,
            whyWrong: Array.isArray(c.primingQuestion.whyWrong) ? c.primingQuestion.whyWrong : null,
            whyWrongHi: Array.isArray(c.primingQuestionHi?.whyWrong) ? c.primingQuestionHi.whyWrong : null,
            embeddedImageUrl: (cardImageUrl && c.primingQuestion.showImage === true) ? cardImageUrl : null,
          });
        }

        // ── ONE POINT PER SLIDE (Duolingo-style) ──
        points.forEach((point, pi) => {
          slides.push({
            type: "concept-point",
            title: pi === 0 ? title : null, // show title only on first point
            subtitle: pi > 0 ? title : null, // show as subtle subtitle on subsequent
            icon: pi === 0 ? icon : null,
            point,
            pointIndex: pi + 1,
            totalPoints: points.length,
            // Attach legacy auto-injected diagram (e.g. interactive-angle widget) to the
            // first point of the first card only — UNLESS this card ships its own
            // imageUrl, in which case the chapter has bespoke art and the generic
            // widget would clutter the slide.
            svgIds: (ci === 0 && pi === 0 && !cardImageUrl) ? conceptSvgIds : [],
            // V2.1-APPLIED: surface schemaVersion on each concept-point so the renderer
            // can cleanly choose between bubble progression (2.1) and legacy single-string (2.0)
            schemaVersion: sv,
            // CONCEPT-IMAGE-APPLIED: embed card.imageUrl at the top of this point only
            // when the point's showImage flag is true. Otherwise the slide renders unchanged.
            embeddedImageUrl: (cardImageUrl && point.showImage === true) ? cardImageUrl : null,
          });

          // ── INTERLEAVE: insert question after every 2nd point ──
          if ((pi + 1) % 2 === 0 && qIdx < microQs.length) {
            slides.push(microQs[qIdx]);
            qIdx++;
          }
        });

        // If odd number of points and we have a question, add after last point
        if (points.length % 2 !== 0 && qIdx < microQs.length) {
          slides.push(microQs[qIdx]);
          qIdx++;
        }

        // CARD-DIAGRAM-APPLIED: card-level `diagram: { src, caption, captionHi }`
        // block. Resolve bare filename to /diagrams/{subj}{cls}/ch{ncert}/ and
        // emit a concept-image slide after the card's points. Cards without a
        // diagram block fall through unchanged.
        if (c.diagram && typeof c.diagram.src === "string" && c.diagram.src.length > 0) {
          const subjectFolderMap = { Maths: "math", Science: "science" };
          const subjFolder = subjectFolderMap[enrichedChapter.subject] || (enrichedChapter.subject || "").toLowerCase();
          const cls = enrichedChapter.class;
          const ncert = enrichedChapter.ncertChapter;
          const src = c.diagram.src.startsWith("/")
            ? c.diagram.src
            : `/diagrams/${subjFolder}${cls}/ch${ncert}/${c.diagram.src}`;
          slides.push({
            type: "concept-image",
            imageUrl: src,
            caption: c.diagram.caption || "",
          });
        }
      });
    }

    // 3. Key difference (if exists)
    if (e.keyDifference) {
      slides.push({
        type: "key-difference",
        title: e.keyDifference.title || "Key difference",
        left: e.keyDifference.left,
        right: e.keyDifference.right,
        rule: e.keyDifference.rule || "",
      });
    }

    // 4. Formula card
    if (e.formulas && e.formulas.length > 0) {
      slides.push({
        type: "formula",
        formulas: e.formulas.map(f => ({
          f: f.f || "",
          vars: f.vars || "",
        })),
        tip: null,
      });

      // Insert remaining question after formula if available
      if (qIdx < microQs.length) {
        slides.push(microQs[qIdx]);
        qIdx++;
      }
    }

    // 5. Worked example
    // PROOF-GALLERY-APPLIED: forward optional `proofGallery` (Ch1 C3) onto the
    // example slide. Renderer shows it AFTER the example block.
    if (e.example) {
      slides.push({
        type: "example",
        problem: e.example.problem || "",
        steps: (e.example.steps || []).map(s => ({
          label: s.label || "",
          value: s.value || "",
        })),
        insight: e.example.insight || "",
        proofGallery: e.proofGallery || null,
      });
    }

    // 6. Memory anchor — funny/weird fact
    // AUDIO-APPLIED: forward optional factAudio (and factHi) through to the slide so
    // renderMemoryAnchor can show a play button. All fields are optional — absent keys
    // leave the slide unchanged.
    if (e.memoryAnchor) {
      slides.push({
        type: "memory-anchor",
        emoji: e.memoryAnchor.emoji || "🧠",
        fact: e.memoryAnchor.fact || "",
        factHi: e.memoryAnchor.factHi || null,
        factAudio: e.memoryAnchor.factAudio || null,
      });
    }

    // 7. Any remaining micro-quiz questions as a batch (fallback)
    // V2-APPLIED: forward questionHi/difficulty/whyWrong to the batch slide
    if (qIdx < microQs.length) {
      slides.push({
        type: "micro-quiz",
        questions: microQs.slice(qIdx).map(q => ({
          // MASTERY-WIRE-APPLIED: preserve original microQuiz index so a question
          // gets the same backend questionId whether it renders interleaved (concept-check)
          // or in this overflow batch.
          microQuizIndex: q.microQuizIndex,
          question: q.question,
          questionHi: q.questionHi,
          options: q.options,
          correct: q.correct,
          explanation: q.explanation,
          difficulty: q.difficulty,
          whyWrong: q.whyWrong,
        })),
      });
    }

    // 8. Board exam questions — from questionPatterns
    if (e.questionPatterns && e.questionPatterns.length > 0) {
      slides.push({
        type: "board-questions",
        questions: e.questionPatterns.flatMap(qp =>
          (qp.sampleQuestions || []).slice(0, 2).map(q => ({
            type: qp.type,
            pattern: qp.pattern,
            question: q,
          }))
        ).slice(0, 4), // max 4 questions shown
      });
    }

    // 9. Hook answer — closes the loop
    if (e.hookAnswer) {
      slides.push({
        type: "hook-answer",
        hookQuestion: e.hook?.question || "",
        answer: e.hookAnswer,
      });
    }

    // V2.1-APPLIED: synthetic concept-boundary slide at the very end of this concept's slide list.
    // Rhythm:
    //   - If this is the FINAL concept in the chapter → "chapter-complete"
    //   - Else if conceptIndex is EVEN (concept 1, 3, 5 in 1-based) → "concept-complete"
    //   - Else (concept 2, 4, 6 in 1-based) → "break-time"
    // Only injected for v2.1 chapters so v1 and v2.0 remain byte-identical.
    if (isV21) {
      const isFinalConcept = conceptIndex === totalConceptCount - 1;
      const isEvenBoundary = (conceptIndex + 1) % 2 === 0; // 1-based: concept 2, 4, 6...
      const nextConcept = enrichedChapter.concepts[conceptIndex + 1] || null;
      const justCompletedCardTitles = (e.concepts || [])
        .map(c => c?.title)
        .filter(t => typeof t === "string" && t.trim().length > 0);

      const base = {
        conceptIndex,
        totalConcepts: totalConceptCount,
        justCompletedName: concept.name,
        justCompletedCardTitles,
        nextConceptName: nextConcept?.name || null,
        nextConceptGoal: nextConcept?.learningGoal || null,
        chapterName: enrichedChapter.chapter,
        subject: enrichedChapter.subject,
      };

      if (isFinalConcept) {
        slides.push({
          type: "chapter-complete",
          ...base,
          // All concept names for the final summary bullet list
          allConceptNames: enrichedChapter.concepts.map(c => c.name),
        });
      } else if (isEvenBoundary) {
        slides.push({ type: "break-time", ...base });
      } else {
        slides.push({ type: "concept-complete", ...base });
      }
    }

    slideData.concepts.push({
      id: concept.id,
      name: concept.name,
      slides,
      // Store question patterns for quiz generation
      questionPatterns: e.questionPatterns || [],
    });
  });

  return slideData;
}

/**
 * Extract question patterns from enriched data for a specific concept
 * Returns array of sample questions across all difficulty levels
 */
export function getQuestionPatterns(enrichedChapter, conceptId) {
  if (!enrichedChapter?.concepts) return [];
  const concept = enrichedChapter.concepts.find(c => c.id === conceptId);
  if (!concept?.enriched?.questionPatterns) return [];
  return concept.enriched.questionPatterns;
}

/**
 * Find ncert_chapter number from curriculum data for a given chapter name
 */
export function findNcertChapter(curriculumData, cls, subject, chapterName) {
  if (!curriculumData?.CBSE?.[String(cls)]?.[subject]) return null;
  const chapters = curriculumData.CBSE[String(cls)][subject].chapters;
  const ch = chapters.find(c => c.name === chapterName);
  return ch?.ncert_chapter || null;
}
